import {InfiniteData, QueryClient, useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import IWBrokerClient from '../client/IWBrokerClient';
import {gatewayRealtimeQueryOptions} from './gatewayQueryUtils';

export type AlarmPrinterLogEntry = {
    id: number;
    timestamp: string | null;
    message: string | null;
};

export const alarmPrinterLogQueryKey = ['alarmPrinterLog'] as const;

const installedAlarmPrinterLogListeners = new WeakSet<QueryClient>();

function installAlarmPrinterLogListener(queryClient: QueryClient) {
    if (installedAlarmPrinterLogListeners.has(queryClient)) {
        return;
    }

    IWBrokerClient.socket.on('alarm printer log', (logEntry: AlarmPrinterLogEntry) => {
        queryClient.setQueryData<InfiniteData<AlarmPrinterLogEntry[], number | undefined>>(
            alarmPrinterLogQueryKey,
            (current: InfiniteData<AlarmPrinterLogEntry[], number | undefined> | undefined) => {
                if (!current?.pages.length) {
                    return current;
                }

                return {
                    pages: [[logEntry, ...current.pages[0]], ...current.pages.slice(1)],
                    pageParams: current.pageParams
                };
            }
        );
    });

    installedAlarmPrinterLogListeners.add(queryClient);
}

export function useAlarmPrinterLogQuery() {
    const queryClient = useQueryClient();
    installAlarmPrinterLogListener(queryClient);

    return useInfiniteQuery({
        queryKey: alarmPrinterLogQueryKey,
        queryFn: async ({pageParam: idBefore}) => {
            return IWBrokerClient.request('alarm printer log', {idBefore}) as Promise<AlarmPrinterLogEntry[]>;
        },
        ...gatewayRealtimeQueryOptions,
        initialPageParam: undefined as number | undefined,
        getNextPageParam: (lastPage: AlarmPrinterLogEntry[]) => {
            return lastPage.length ? lastPage[lastPage.length - 1].id : undefined;
        }
    });
}
